import {observer} from 'mobx-react-lite'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Lang} from 'yougile-lang'
import {LinkButtonNew} from '@components/common/base/buttons'
import {Help} from '@components/common/base/help'
import {SelectWithDropdown} from '@components/common/base/select-with-dropdown'
import {SelectWithCheckboxes} from '@components/common/base/select-with-dropdown/select-with-checkboxes'
import {Table} from '@components/common/base/table/table'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {byTimeGlobalStore} from '@components/statistics/by-time/by-time-global-store'
import {useByTimeStore} from '@components/statistics/by-time/by-time-store'
import {ColumnsChart} from '@components/statistics/by-time/columns-chart/columns-chart'
import {TwColumnChartHeader} from '@components/statistics/by-time/columns-chart/containers'
import {
  AdditionalFilters,
  ByTimeContainer,
  ByTimeHeader,
  ByTimeHeaderFilters,
  TwNotFound
} from '@components/statistics/by-time/containers'
import {Filters} from '@components/statistics/by-time/filters'
import {
  FirstColumnWidth,
  useContentWidth,
  useTableChartData,
  useTableColumns
} from '@components/statistics/by-time/hooks'
import {ResetFiltersBlock} from '@components/statistics/by-time/reset-filters-block'
import {WithResizeObserver} from '@components/sticker/sticker-panel/resize-observer'
import {useBoolean, useSyncEvent} from '@containers/hooks'

// eslint-disable-next-line max-lines-per-function
export const BoardView = observer(() => {
  const {selectedProjectId, selectedBoardId} = byTimeGlobalStore.state
  const [state, store] = useByTimeStore()
  const firstLoad = useRef<boolean>(false)
  const [showFilters, setShowFilters] = useState<boolean>(false)
  const onTop = useBoolean(true)
  const tableContainer = useRef<HTMLDivElement>(null)
  const scrollContainer = useRef<HTMLElement>()
  const headerInnerContainer = useRef<HTMLDivElement>()
  const [screenIsSmall, setScreenIsSmall] = useState(false)

  useEffect(() => {
    if (
      state.projectList.length &&
      (!selectedProjectId ||
        !state.projectList.find(item => item.key === selectedProjectId))
    ) {
      byTimeGlobalStore.onSelectProject(state.projectList[0].key)
    }
  }, [state.projectList])

  useEffect(() => {
    if (selectedProjectId) {
      store.syncBoardList()
    }
  }, [selectedProjectId])

  useEffect(() => {
    if (state.boardList.length && !firstLoad.current) {
      if (
        !selectedBoardId ||
        !state.boardList.find(item => item.key === selectedBoardId)
      ) {
        store.onSelectBoard(state.boardList[0])
      }
      firstLoad.current = true
    }
  }, [state.boardList])

  useEffect(() => {
    store.resetChangeColumnFilter()
    store.setDefaultFilterValues()
    store.syncBoardTasksIds()
  }, [selectedBoardId])

  useEffect(() => {
    if (state.setToTop) {
      if (scrollContainer.current) {
        scrollContainer.current.scrollTop = 0
      }
      store.setToTop(false)
    }
  }, [state.setToTop])

  const tableColumns = useTableColumns(store.filteredBoardColumns)

  const {tableData, chartData} = useTableChartData(
    store.filteredBoardColumns,
    store.boardTasks
  )

  useSyncEvent('current', {filter: '*', debounceTime: 2000}, () => {
    store.syncBoardTasksIds()
  })

  const onResizeHeader = useCallback(() => {
    if (!headerInnerContainer.current) {
      return
    }
    setScreenIsSmall(headerInnerContainer.current.offsetWidth < 1010)
  }, [headerInnerContainer.current])

  const contentWidth = useContentWidth(tableColumns.length)

  return (
    <WithResizeObserver className="h-full" type="w" callback={onResizeHeader}>
      <ByTimeContainer ref={headerInnerContainer}>
        <WithPerfectScrollbar
          containerRef={el => {
            scrollContainer.current = el
          }}
          className="h-full"
          scrollDirection="xy"
          onScrollY={x => {
            const scrollOnTop = x.scrollTop === 0
            onTop.setValue(scrollOnTop)
            store.setHeaderShadow(!scrollOnTop)

            const tableOffsetTop = tableContainer.current?.offsetTop
            if (tableOffsetTop) {
              store.setButtonTopVisible(x.scrollTop >= tableOffsetTop)
            }
          }}
        >
          <ByTimeHeader>
            <ByTimeHeaderFilters $smallScreen={screenIsSmall}>
              <div className="flex gap-8 items-center">
                <div className="whitespace-nowrap text-14">
                  {Lang.msg.report.byTimeFilterTitle}:
                </div>
                <SelectWithDropdown
                  variant="new"
                  className="min-w-[180px] max-w-[180px]"
                  value={
                    state.projectList.find(
                      item => item.key === selectedProjectId
                    )?.text
                  }
                  options={state.projectList}
                  onChange={option => {
                    byTimeGlobalStore.onSelectProject(option.key)
                    byTimeGlobalStore.onSelectBoard(undefined)
                  }}
                />
                <SelectWithDropdown
                  variant="new"
                  className="max-w-[150px] min-w-[150px]"
                  value={
                    state.boardList.find(item => item.key === selectedBoardId)
                      ?.text || Lang.msg.component.dropdown.notSelected
                  }
                  options={state.boardList}
                  onChange={store.onSelectBoard}
                />
              </div>
              <div className="ml-8 flex gap-16">
                <LinkButtonNew
                  icon="filter"
                  title={Lang.msg.report.additionalFiltering}
                  onClick={() => setShowFilters(prev => !prev)}
                  iconAfter={
                    showFilters
                      ? 'IconCustomChevronUp'
                      : 'IconCustomChevronDown'
                  }
                />
                {!!store.filtersSummary && <ResetFiltersBlock />}
              </div>
            </ByTimeHeaderFilters>
            {showFilters && (
              <AdditionalFilters>
                <Filters />
              </AdditionalFilters>
            )}
          </ByTimeHeader>
          <div className="flex-1 px-24 py-24">
            {!state.emptyBoard &&
              (Boolean(tableData.length) ||
                store.columnsFilterValue === 'Не выбрано') && (
                <TwColumnChartHeader
                  style={{
                    paddingLeft: `${FirstColumnWidth}px`
                  }}
                >
                  <div className="flex font-semibold">
                    {Lang.msg.averageTimeInColumns}
                    <div className="ml-4">
                      <Help iconSize="medium" name="columnReport" />
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="text-14">{Lang.msg.report.display}:</div>
                    <div className="ml-8">
                      <SelectWithCheckboxes
                        disabled={!selectedBoardId}
                        variant="new"
                        className="min-w-[150px] max-w-[250px] text-14"
                        options={store.locationOptions}
                        value={store.columnsFilterValue}
                        allCaption={Lang.msg.allColumns}
                        onChange={store.onChangeColumnFilter}
                      />
                    </div>
                  </div>
                </TwColumnChartHeader>
              )}
            {state.emptyBoard && !tableData.length && (
              <TwNotFound>
                <div className="text-16 font-bold">
                  {Lang.msg.report.emptyBoard}
                </div>
              </TwNotFound>
            )}
            {!state.emptyBoard &&
              !tableData.length &&
              selectedBoardId &&
              store.columnsFilterValue !==
                Lang.msg.component.dropdown.notSelected && (
                <TwNotFound>
                  <div className="text-16 font-bold">
                    {Lang.msg.report.nothingIsFound}
                  </div>
                  <div className="text-14">{Lang.msg.report.tryReset}</div>
                  <LinkButtonNew onClick={store.setDefaultFilterValues}>
                    {Lang.msg.statsTimeline.clearFilters}
                  </LinkButtonNew>
                </TwNotFound>
              )}
            {Boolean(tableData.length) && (
              <>
                <div
                  style={{
                    paddingLeft: `${FirstColumnWidth}px`
                  }}
                >
                  <div
                    className="h-[212px]"
                    style={{
                      width: `${contentWidth}px`
                    }}
                  >
                    <ColumnsChart
                      chartData={{
                        data: chartData
                      }}
                    />
                  </div>
                </div>
                {tableColumns && (
                  <div ref={tableContainer}>
                    <Table
                      withPerfectScrollbar={false}
                      headerShadow={state.showHeaderShadow}
                      withHeaderShadow
                      stripeBackground
                      withDragScroll
                      columns={tableColumns}
                      rows={tableData}
                      contentWidth={contentWidth + FirstColumnWidth}
                      headClassName="h-auto py-12"
                    />{' '}
                  </div>
                )}
              </>
            )}
          </div>
        </WithPerfectScrollbar>
      </ByTimeContainer>
    </WithResizeObserver>
  )
})
